import {
  HttpClient,
  HttpEvent,
  HttpEventType, HttpProgressEvent,
  HttpResponse,
} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {
  Job,
  JobApiService, JobFile,
  JobFilesApiService,
} from '@ansys/cloud-angular-client';
import { State, AuthTokenState } from '@ansys-internal/awf-angular/store';
import {
  BehaviorSubject,
  Observable,
  lastValueFrom,
  tap,
  throwError,
  startWith,
  takeUntil,
} from 'rxjs';
import { JobsService, RequirementsSolved, UtilitiesService } from 'src/api';
import { ResultType } from 'src/app/components/widgets/results-list/results-list.component';
import { RequirementsService } from './requirements.service';
import { DataDisplayService, DataDisplayState } from './data-display.service';
import { environment } from 'src/environments/environment';
import {NavigateAction, SnackBarService} from '@ansys-internal/awf-angular/shared';
import { catchError } from 'rxjs/operators';
import {
  ActionContributionsService,
  WorkspaceTabSection,
} from '@ansys-internal/awf-angular/contributions';
import { ComponentSection } from '../../awf-contributions/tabs/concept-tab/sections/component-section/component-section';
import { ConceptSubSection } from '../enums';
import {
  ActiveSectionState,
  SectionSelectAction,
} from '@ansys-internal/awf-angular/workspace';
import { CompareJobsExecuteEnum } from '../../state/lib/compare-jobs.state';
import { CevActionsService } from './cev-actions.service';
import { JobState } from '../../state/lib/jobs.state';
import { JobLoadingStatus } from '../enums/job-loading-status.enum';
import {FileService, optionsType} from "./file.service";

@Injectable({ providedIn: 'root' })
export class ResultsService {
  private fileService = inject(FileService);
  private solveAPI: string = environment.ocmUrl;
  private jobNames: Record<string, string> = {};
  public localDevOutput$ = new BehaviorSubject<RequirementsSolved[] | null>(null);

  constructor(
    private jobFilesService: JobFilesApiService,
    private jobService: JobsService,
    private jobApi: JobApiService,
    private requirementService: RequirementsService,
    private utilService: UtilitiesService,
    private state: State,
    private snackbar: SnackBarService,
    private displayService: DataDisplayService,
    private http: HttpClient,
    private actions: ActionContributionsService,
    private cevActions: CevActionsService
  ) {
    this.state
      .get(ActiveSectionState)
      .subscribe((section) => {
        if (!section) {
          return;
        }

        if (section?.urlParams?.[1] === ConceptSubSection.COMPARISON_TOOL) {
          this.handleComparisonToolSelection(section);
        }
      });

    this.state
      .get(JobState)
      .pipe(startWith(this.state.value(JobState)))
      .subscribe((jobs: Job[]) => {
        this.mapJobNames(jobs);
      });
  }

  async getResults(
    job: Job,
    retry: boolean = true,
    results: RequirementsSolved[] | null = null
  ): Promise<{ results: RequirementsSolved[]; cliVersion?: string }> {
    const validJob = await this.ensureJobHasSimulationId(job, retry);

    let cliVersion: string | undefined;

    if (!results) {
      this.requirementService.jobResultLoading.next(JobLoadingStatus.PROGRESS);
      try {
        const download = await this.downloadResults(validJob);
        results = download.results;
        cliVersion = download.cliVersion;
      } catch (error) {
        throw error;
      }
    }

    if (results) {
      this.processResults(results);
    } else {
      throw new Error('No results available to process');
    }

    this.displayService.displayState = DataDisplayState.REQUIREMENT_HELP;

    return { results, cliVersion };
  }

  async ensureJobHasSimulationId(job: Job, retry: boolean): Promise<Job> {
    if (!job.simulations?.[0]?.simulationId) {
      if (retry) {
        const updatedJob = await lastValueFrom(
          this.jobApi.jobLoadHandler({ jobId: job.jobId as string })
        );
        return this.ensureJobHasSimulationId(updatedJob, false);
      } else {
        this.snackbar.error('No simulation id, please refresh the page');
        throw new Error('No simulation id');
      }
    }
    return job;
  }

  private processResults(results: RequirementsSolved[]): void {
    const staticResults = results.filter(
      (result) => result.requirement_solved_type === ResultType.STATIC
    );
    const dynamicResults = results.filter(
      (result) => result.requirement_solved_type === ResultType.DYNAMIC
    );
    const dcResults = results.filter(
      (result) => result.requirement_solved_type === ResultType.DRIVE_CYCLE
    );

    if (staticResults.length) {
      this.requirementService.processStaticResultData(staticResults);
    }
    if (dynamicResults.length) {
      this.requirementService.processDynamicResultData(dynamicResults);
    }
    if (dcResults.length) {
      this.requirementService.processDriveCycleResults(dcResults);
    }
  }

  async downloadResults(job: Job): Promise<{ results: RequirementsSolved[]; cliVersion?: string; }> {
    const currentVersion = await lastValueFrom(
      this.utilService.getDataFormatVersionNumberUtilitiesDataFormatVersionGet()
    );

    const fileList = await lastValueFrom(
      this.jobFilesService.getFileListByJobIdSimulationId(
        job.jobId,
        job.simulations?.[0]?.simulationId
      )
    );

    const findFile = (identifiers: string[]): JobFile | undefined => fileList.find((file) => identifiers.some((identifier) => file.fileName?.includes(identifier)));
    const outputFile = findFile([`${job.simulations?.[0]?.simulationId}/output_file_v${currentVersion}`,]);

    const cliVersionFileToDownload = findFile([
      `${job.simulations?.[0]?.simulationId}/cli_version.txt`,
      `${job.simulations?.[0]?.simulationId}/solver_version.txt`,
    ]);

    let fileName = outputFile?.fileName?.split('/')[1] ?? '';
    let fileSize = outputFile?.fileSize ?? 0;
    this.displayService.updateProgressItem(0);

    if (!outputFile) {
      this.displayService.message = 'Converting results to latest version';
      let filteredFileList = fileList.filter(
        (file) => !file.fileName?.includes('cli_version')
      );
      filteredFileList = filteredFileList.filter(
        (file) => !file.fileName?.includes('solver_version')
      );
      filteredFileList = filteredFileList.filter(
        (file) => !file.fileName?.includes('filtered')
      );
      filteredFileList = filteredFileList.filter(
        (file) => !file.fileName?.includes('console.log')
      );
      const updateResults = await lastValueFrom(
        this.jobService.updateResultsFileDataFormatJobsDataCompatibilityConversionPost(
          // todo this logic isn't great, relies on the latest file being in the correct order in the list
          filteredFileList.reverse()[0]?.fileName?.split('/')[1] as string,
          {
            job_id: job.jobId as string,
            simulation_id: job.simulations?.[0]?.simulationId as string,
            docker_tag: job.dockerTag as string,
            job_name: job.jobName as string,
          },
          job.designId as string
        )
      );
      fileName = updateResults.file_name;
      fileSize = updateResults.file_size;
    }

    if (job.simulations?.[0]?.simulationId && job.jobId) {
      this.displayService.message = 'Downloading Results';
      try {
        const outputFileUri = outputFile?.downloadRequest?.uri ?? '';
        const outputFileSize = outputFile?.fileSize ?? 0;
        const payloadOptions: optionsType = {
          responseType: 'json',
          observe: 'events',
          reportProgress: true,
          onProgress: (event: HttpEvent<any>, s: any) => {
            if (event.type === HttpEventType.DownloadProgress) {
              const progressEvent = event as HttpProgressEvent;
              this.displayService.updateProgressItem((progressEvent.loaded / (s ?? 0)) * 100);
            }
          }
        };
        let file;

        try {
          // Try NON‑S3 first
          file = await lastValueFrom(this.downloadJsonFile(job.jobId, job.simulations?.[0]?.simulationId, fileName, fileSize));
        } catch (err) {
          console.warn('Non‑S3 download failed, falling back to S3', err);

          // Fallback to S3
          file = await lastValueFrom(this.fileService.downloadFileFromUrl(outputFileUri, payloadOptions, outputFileSize));
        }
        let cliVersion: string | undefined;

        if (cliVersionFileToDownload) {
          const cliVersionFileName = cliVersionFileToDownload.fileName?.split('/').pop();
          cliVersion = await lastValueFrom(this.downloadTextFile(job.jobId, job.simulations?.[0]?.simulationId, cliVersionFileName as string));
        }

        const results = ((file as HttpResponse<unknown>)['body'] as RequirementsSolved[])
          .map((result, index) => {
            result.id = job.jobId + '_' + index.toString();
            result.jobId = job.jobId;
            return result;
          });

        return { results, cliVersion };
      } catch (error) {
        throw new Error('Error downloading results');
      }
    } else {
      throw new Error('No file to download');
    }
  }

  public async downloadOutputFile(job: Job): Promise<Blob> {
    const fileList = await lastValueFrom(
      this.jobFilesService.getFileListByJobIdSimulationId(
        job.jobId,
        job.simulations?.[0]?.simulationId
      )
    );

    const findFile = (identifiers: string[]): JobFile | undefined =>
      fileList.find((file) =>
        identifiers.some((id) => file.fileName?.includes(id))
      );

    const consoleLogFile = findFile([`${job.simulations?.[0]?.simulationId}/console.log`]);

    if (!consoleLogFile) {
      throw new Error('console.log file not found in job file list');
    }

    const fileName = consoleLogFile.fileName?.split('/')[1] ?? '';
    const fileSize = consoleLogFile.fileSize ?? 0;
    const fileUri = consoleLogFile.downloadRequest?.uri ?? '';

    const payloadOptions: optionsType = {
      responseType: 'blob' as any,
      observe: 'events',
      reportProgress: false,
    };

    let file: HttpEvent<any>;
    try {
      // Try NON-S3 first
      file = await lastValueFrom(
        this.downloadJsonFile(
          job.jobId as string,
          job.simulations?.[0]?.simulationId as string,
          fileName,
          fileSize
        )
      );
    } catch (err) {
      console.warn('Non-S3 download failed, falling back to S3', err);
      // Fallback to S3
      file = await lastValueFrom(
        this.fileService.downloadFileFromUrl(fileUri, payloadOptions, fileSize)
      );
    }

    const body = (file as HttpResponse<unknown>)['body'];
    if (body instanceof Blob) {
      return body;
    }

    return new Blob([JSON.stringify(body)], { type: 'application/json' });
  }


  private downloadFileWithRetry<T>(
    jobId: string,
    simulationId: string,
    fileName: string,
    options: {
      responseType: 'json' | 'text';
      observe?: 'events';
      reportProgress?: boolean;
      onProgress?: (event: HttpEvent<any>, size?: number) => void;
    },
    size?: number
  ): Observable<any> {
    const midPoint = fileName.includes('converted') ? '' : 'decrypted/';

    const makeRequest = (mp: string) =>
      this.http.get<T>(
        `${this.solveAPI}/job/files/${mp}${jobId}/${simulationId}/${fileName}`,
        {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${this.state.value(AuthTokenState)}`,
          },
        } as any
      );

    return makeRequest(midPoint).pipe(
      tap((event: any) => {
        if (options.onProgress && event.type === HttpEventType.DownloadProgress) {
          options.onProgress(event, size);
        }
      }),
      catchError((err) => {
        console.warn('Decrypt mode failed, retrying without midpoint…');
        return makeRequest('').pipe(
          catchError((err2) => {
            console.error('Second attempt also failed:', err2);
            return throwError(() => err2);
          })
        );
      })
    );
  }

  downloadTextFile(
    jobId: string,
    simulationId: string,
    fileName: string
  ): Observable<string> {
    return this.downloadFileWithRetry<string>(jobId, simulationId, fileName, {
      responseType: 'text'
    });
  }

  downloadJsonFile(
    jobId: string,
    simulationId: string,
    fileName: string,
    size: number = 0
  ): Observable<HttpEvent<any>> {
    return this.downloadFileWithRetry<HttpEvent<any>>(jobId, simulationId, fileName, {
      responseType: 'json',
      observe: 'events',
      reportProgress: true,
      onProgress: (event, s) => {
        if (event.type === HttpEventType.DownloadProgress) {
          const progressEvent = event as HttpProgressEvent;
          this.displayService.updateProgressItem((progressEvent.loaded / (s ?? 0)) * 100);
        }
      }
    }, size);
  }

  /**
   * Request Job File
   * Get contents of console.log.
   * @param jobId
   * @param fileName
   * @returns string Successful Response
   * @throws ApiError
   */
  public getJobFileAsBlob(jobId: string, fileName: string): Observable<Blob> {
    return this.http.get(`${this.solveAPI}/job/files/${jobId}/${fileName}`, {
      headers: {
        Accept: 'application/octet-stream',
        Authorization: `${this.state.value(AuthTokenState)}`,
      },
      responseType: 'blob' as const,
    });
  }

  /**
   * Request Job File
   * Get contents of console.log.
   * @param jobId
   * @param fileName
   * @returns string Successful Response
   * @throws ApiError
   */
  public getJobFile(jobId: string, fileName: string): Observable<object> {
    return this.http.get(`${this.solveAPI}/job/files/${jobId}/${fileName}`, {
      headers: {
        Accept: 'application/octet-stream',
        Authorization: `${this.state.value(AuthTokenState)}`,
      },
    });
  }

  /**
   * Cancel all simulations in job
   *
   * @param jobId
   */
  public cancelJob(jobId: string): Observable<object> {
    return this.http.post(`${this.solveAPI}/job/stop/`, { jobId });
  }

  public getJobProgress(jobId: string): Observable<JobProgressResponse> {
    return this.http
      .post<JobProgressResponse>(`${this.solveAPI}/job/progress/`, { jobId })
      .pipe(
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  private mapJobNames(jobs: Job[]): void {
    this.jobNames = jobs.reduce((map, job) => {
      if (job.jobId !== undefined) {
        map[job.jobId] = job.jobName ?? 'Unknown';
      }
      return map;
    }, {} as Record<string, string>);
  }

  public getJobName(jobId: string): string {
    return this.jobNames[jobId] ?? 'Unknown';
  }

  public jobUpdateName(jobId: string, jobName: string): Observable<Job> {
    return this.http.post<Job>(
      `${this.solveAPI}/job/update/name`,
      {
        jobId,
        jobName,
      },
      {
        responseType: 'json',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${this.state.value(AuthTokenState)}`,
        },
      }
    );
  }

  protected compareJobs: any[] = [];
  private activeType: 'static' | 'dynamic' | 'drive_cycle' | null = null;

  private async handleComparisonToolSelection(
    section: WorkspaceTabSection
  ): Promise<void> {
    if (section?.urlParams?.[1] !== ConceptSubSection.COMPARISON_TOOL) {
      throw Error('Not a comparison tool section');
    }

    if (!section?.urlParams?.[2]) {
      throw Error("no id's provided");
    }
    const compareIds = section.urlParams[2].split(',');

    const allRequirementTypes = new Set<string>();
    this.compareJobs = [];

    this.cevActions.execute(
      CompareJobsExecuteEnum.CLEAR_RESULTS_FROM_COMPARE,
      null
    );

    const jobs: Job[] = [];
    for (const jobId of compareIds) {
      const updatedJob = await lastValueFrom(
        this.jobApi.jobLoadHandler({ jobId })
      );
      const validJob = await this.ensureJobHasSimulationId(updatedJob, false);

      const existingIndex = jobs.findIndex((j) => j.jobId === validJob.jobId);
      if (existingIndex !== -1) {
        jobs[existingIndex] = validJob;
      } else {
        jobs.push(validJob);
      }

      const { results, cliVersion } = await this.getResults(validJob);
      results.forEach((r) => {
        if (r.requirement_solved_type) {
          allRequirementTypes.add(r.requirement_solved_type);
        }
      });

      const compareResult = { jobId: validJob.jobId, cliVersion, results };
      const compareIndex = this.compareJobs.findIndex(
        (r) => r.jobId === validJob.jobId
      );

      /** Add or update result in compareJobs */
      if (compareIndex !== -1) {
        this.compareJobs = [
          ...this.compareJobs.slice(0, compareIndex),
          compareResult,
          ...this.compareJobs.slice(compareIndex + 1),
        ];
      } else {
        this.compareJobs = [...this.compareJobs, compareResult];
      }
    }

    // Dynamically choose the first available type to show
    if (allRequirementTypes.has('static')) {
      this.activeType = 'static';
    } else if (allRequirementTypes.has('dynamic')) {
      this.activeType = 'dynamic';
    } else if (allRequirementTypes.has('drive_cycle')) {
      this.activeType = 'drive_cycle';
    } else {
      this.activeType = null;
    }

    this.cevActions.execute(
      CompareJobsExecuteEnum.SET_JOBS,
      this.compareJobs as any
    );
  }
}

export interface JobProgressResponse {
  jobId: string;
  simulationProgressList: {
    progress: number;
    simulationId: string;
  }[];
}
